// DipSiteDetail — slide-over card for a tapped dip-site on the map.
//
// Layout mirrors PirepDetail / WxCamDetail (right-anchored, bottom-20,
// w-96 max). Three sections:
//   1. Header — name + ownership chip + close
//   2. Rollup — sourceType, ownership, managing agency / owner,
//      reimbursement status, lat/lon
//   3. Recent uses — last ~10 dip_site_uses rows for this site,
//      newest first, with aircraft + gallons + when.
//   4. Footer actions — "Re-look up ownership" (calls /api/landowner
//      and patches the site if the answer changed).
//
// Reads directly from Supabase on open; no realtime needed — the user
// can tap "Refresh" to re-pull. Lightweight because dip-sites are
// low-volume (single-org, hundreds of rows max nationwide).

import { useEffect, useState } from 'react';
import { Droplet, RefreshCw, AlertTriangle } from '../shell/Icon';
import { supabase } from '../lib/supabase';
import {
  lookupLandowner,
  updateDipSiteOwnership,
  type DipSite,
  type DipSiteOwnershipClass,
} from '../lib/dipSites';
import { formatLatLonDMM } from '../lib/coords';
import { formatLocalTime } from '../lib/timeFormat';
import DetailCard, { RollupRow } from '../components/DetailCard';

export interface DipSiteSeed {
  dipSiteId: string;
  name: string;
  ownershipClass: DipSiteOwnershipClass | string;
  managingAgency: string | null;
  ownerName: string | null;
  reimbursementStatus: string;
  sourceType: string;
  lat: number;
  lon: number;
}

interface DipUseRow {
  id: string;
  flight_id: string | null;
  flight_client_id: string | null;
  aircraft_client_id: string | null;
  filled_at: string;
  gallons: number | null;
  liters: number | null;
  suppressant: string | null;
  notes: string | null;
}

interface Props {
  seed: DipSiteSeed;
  onClose: () => void;
}

// Ownership color matches DIPSITE_OWNERSHIP_COLOR_EXPR in dipSitesLayer.ts.
const OWNERSHIP_DOT: Record<string, string> = {
  federal: 'bg-emerald-500',
  private: 'bg-amber-500',
  tribal:  'bg-purple-500',
  state:   'bg-blue-500',
  unknown: 'bg-gray-400',
};

const OWNERSHIP_LABEL: Record<string, string> = {
  federal: 'Federal',
  private: 'Private',
  tribal:  'Tribal',
  state:   'State',
  unknown: 'Unknown',
};

export default function DipSiteDetail({ seed, onClose }: Props) {
  const [uses, setUses] = useState<DipUseRow[]>([]);
  const [loadingUses, setLoadingUses] = useState(true);

  // Local copy of ownership fields so a successful re-lookup updates
  // the card immediately without waiting on a parent re-render.
  const [ownershipClass, setOwnershipClass] = useState<string>(seed.ownershipClass);
  const [managingAgency, setManagingAgency] = useState<string | null>(seed.managingAgency);
  const [ownerName, setOwnerName] = useState<string | null>(seed.ownerName);
  const [reimbursementStatus, setReimbursementStatus] = useState<string>(seed.reimbursementStatus);
  const [reLookupBusy, setReLookupBusy] = useState(false);
  const [reLookupMsg, setReLookupMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supabase) { setLoadingUses(false); return; }
      const sb = supabase;
      setLoadingUses(true);
      const { data, error } = await sb
        .schema('app')
        .from('dip_site_uses')
        .select('id, flight_id, flight_client_id, aircraft_client_id, filled_at, gallons, liters, suppressant, notes')
        .eq('dip_site_id', seed.dipSiteId)
        .order('filled_at', { ascending: false })
        .limit(10);
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('[DipSiteDetail] uses query failed:', error.message);
      }
      setUses((data ?? []) as DipUseRow[]);
      setLoadingUses(false);
    })();
    return () => { cancelled = true; };
  }, [seed.dipSiteId]);

  async function reLookup() {
    setReLookupBusy(true);
    setReLookupMsg(null);
    const r = await lookupLandowner(seed.lat, seed.lon);
    if (r.ownershipClass === 'unknown' && r.source.length === 0) {
      setReLookupMsg('Lookup unavailable — leaving as-is.');
      setReLookupBusy(false);
      return;
    }
    const changed =
      r.ownershipClass !== ownershipClass ||
      r.managingAgency !== managingAgency ||
      r.ownerName !== ownerName;
    if (!changed) {
      setReLookupMsg('No change — ownership info unchanged.');
      setReLookupBusy(false);
      return;
    }
    const upd = await updateDipSiteOwnership(seed.dipSiteId, {
      ownershipClass: r.ownershipClass,
      managingAgency: r.managingAgency,
      ownerName: r.ownerName,
      parcelId: r.parcelId,
      ownerContact: r.ownerContact,
    });
    if (upd.ok) {
      setOwnershipClass(r.ownershipClass);
      setManagingAgency(r.managingAgency);
      setOwnerName(r.ownerName);
      if (r.ownershipClass === 'federal') setReimbursementStatus('not_required');
      else if (r.ownershipClass === 'private') setReimbursementStatus('pending');
      setReLookupMsg(`Updated — ${OWNERSHIP_LABEL[r.ownershipClass] ?? r.ownershipClass}.`);
    } else {
      setReLookupMsg(`Save failed: ${upd.error ?? 'unknown error'}`);
    }
    setReLookupBusy(false);
  }

  const totalGal = uses.reduce((acc, u) => acc + (u.gallons ?? 0), 0);
  const dotClass = OWNERSHIP_DOT[ownershipClass] ?? OWNERSHIP_DOT.unknown;
  const ownershipLabel = OWNERSHIP_LABEL[ownershipClass] ?? ownershipClass;

  const reimbursementWarn =
    ownershipClass === 'private' &&
    reimbursementStatus !== 'paid' &&
    reimbursementStatus !== 'waived';

  // Eyebrow row: label + Droplet icon at far right.
  const eyebrowNode = (
    <>
      <span className="text-[10px] tracking-widest text-cockpit-300">
        DIP SITE · {ownershipLabel.toUpperCase()}
      </span>
      <Droplet size={11} className="text-cyan-400 ml-auto" />
    </>
  );

  return (
    <DetailCard
      headerDot={dotClass}
      eyebrow={eyebrowNode}
      title={seed.name}
      subtitle={`${seed.sourceType} · ${formatLatLonDMM(seed.lat, seed.lon)}`}
      onClose={onClose}
    >
      <div className="space-y-3">
        {/* Rollup */}
        <div className="bg-cockpit-700/20 border border-cockpit-700 rounded-md px-3 py-2 space-y-0.5">
          <RollupRow k="Ownership" v={ownershipLabel} />
          {managingAgency && <RollupRow k="Agency" v={managingAgency} />}
          {ownerName && <RollupRow k="Owner" v={ownerName} />}
          {reimbursementStatus && reimbursementStatus !== 'not_required' && (
            <RollupRow
              k="Reimbursement"
              v={reimbursementStatus}
              valueClass={reimbursementWarn ? 'text-amber-300' : undefined}
            />
          )}
        </div>

        {/* Recent uses */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] tracking-widest text-cockpit-400 uppercase">Recent dips</span>
            {uses.length > 0 && (
              <span className="text-[10px] font-mono text-cyan-300">
                {uses.length} · {totalGal.toLocaleString()} gal
              </span>
            )}
          </div>
          {loadingUses ? (
            <div className="text-xs text-cockpit-400 italic px-1">Loading…</div>
          ) : uses.length === 0 ? (
            <div className="text-xs text-cockpit-400 italic px-1">No dip events recorded yet.</div>
          ) : (
            <ul className="space-y-1">
              {uses.map((u) => (
                <li
                  key={u.id}
                  className="bg-cockpit-750 border border-cockpit-700 rounded px-2 py-1.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-cockpit-100 truncate">
                      {u.aircraft_client_id ?? (
                        <span className="text-cockpit-400 italic">Unassigned aircraft</span>
                      )}
                    </div>
                    <div className="text-xs font-mono text-cyan-300 shrink-0">
                      {u.gallons != null ? `${u.gallons.toLocaleString()} gal` : '—'}
                    </div>
                  </div>
                  <div className="text-[10px] text-cockpit-400 flex items-center gap-1.5 mt-0.5">
                    <span>{formatLocalTime(u.filled_at)}</span>
                    {u.suppressant && u.suppressant !== 'water' && (
                      <span className="px-1 rounded bg-cockpit-700 border border-cockpit-600">
                        {u.suppressant}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Re-lookup action */}
        <div className="pt-1 border-t border-cockpit-700/60">
          <button
            onClick={() => { void reLookup(); }}
            disabled={reLookupBusy}
            className="w-full flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-cockpit-700 hover:bg-cockpit-600 disabled:opacity-50 border border-cockpit-600 text-cockpit-100"
          >
            <RefreshCw size={12} className={reLookupBusy ? 'animate-spin' : ''} />
            {reLookupBusy ? 'Looking up…' : 'Re-look up ownership'}
          </button>
          {reLookupMsg && (
            <div className="mt-1.5 text-[11px] text-cockpit-300 flex items-start gap-1.5">
              {reLookupMsg.startsWith('Save failed') && (
                <AlertTriangle size={11} className="text-amber-400 shrink-0 mt-px" />
              )}
              <span>{reLookupMsg}</span>
            </div>
          )}
        </div>
      </div>
    </DetailCard>
  );
}
